import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  MessageCircleHeart,
  Pencil,
  Sparkles,
  Clock,
  CheckCircle2,
  ThumbsUp,
  ThumbsDown,
  HelpCircle,
  MessageSquare,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

const API = "/api";

const ROLE_LABELS = {
  mentor: "Mentor",
  mentee: "Mentee",
  professor: "Professor",
  student: "Student",
  coworker: "Coworker",
  coach: "Coach",
  coachee: "Coachee",
  business_partner: "Business Partner",
  therapist: "Therapist",
  family: "Family",
  friend: "Friend",
  other: "Other",
};

const ROLE_COLORS = {
  mentor: "#22c55e",
  mentee: "#86efac",
  professor: "#059669",
  student: "#0ea5e9",
  coworker: "#8b5cf6",
  coach: "#eab308",
  coachee: "#f97316",
  business_partner: "#6366f1",
  therapist: "#ec4899",
  family: "#e11d48",
  friend: "#3b82f6",
  other: "#94a3b8",
};

const formatRelativeTime = (iso) => {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

const ConnectionDrawer = ({
  node,
  open,
  onOpenChange,
  onRoleChange,
  onRemove,
  onAskInsight,
}) => {
  const [editingRole, setEditingRole] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [insightsData, setInsightsData] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [openVoteGroup, setOpenVoteGroup] = useState(null); // "agree" | "disagree" | "unsure" | null

  useEffect(() => {
    if (!open || !node?.connection_id || node.status === "pending") {
      setInsightsData(null);
      setOpenVoteGroup(null);
      return;
    }
    let cancelled = false;
    setInsightsLoading(true);
    (async () => {
      try {
        const res = await fetch(`${API}/network/${node.connection_id}/insights`, {
          credentials: "include",
        });
        if (!res.ok) return;
        const d = await res.json();
        if (!cancelled) setInsightsData(d);
      } catch {} finally {
        if (!cancelled) setInsightsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, node?.connection_id, node?.status]);

  if (!node) return null;

  const roleColor = ROLE_COLORS[node.role] || ROLE_COLORS.other;
  const isPending = node.status === "pending";
  const lastInsight = formatRelativeTime(node.last_insight_at);

  const handleRoleChange = async (newRole) => {
    try {
      const res = await fetch(`${API}/network/${node.connection_id}/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole }),
      });
      if (res.ok) {
        toast.success("Relationship updated");
        setEditingRole(false);
        onRoleChange?.(node.connection_id, newRole);
      } else {
        toast.error("Failed to update relationship");
      }
    } catch {
      toast.error("Failed to update relationship");
    }
  };

  const handleRemove = async () => {
    try {
      const res = await fetch(`${API}/network/${node.connection_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        toast.success(`${node.name} removed from your circle`);
        setConfirmRemove(false);
        onOpenChange(false);
        onRemove?.(node.connection_id);
      } else {
        toast.error("Failed to remove connection");
      }
    } catch {
      toast.error("Failed to remove connection");
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md overflow-y-auto"
          data-testid="connection-drawer"
        >
          <SheetHeader className="text-left">
            <SheetTitle className="sr-only">Connection details</SheetTitle>
          </SheetHeader>

          {/* Avatar + name header */}
          <div className="flex flex-col items-center text-center pt-4 pb-6 border-b border-slate-100">
            <div
              className="rounded-full overflow-hidden"
              style={{
                width: 96,
                height: 96,
                border: `3px solid ${isPending ? "#cbd5e1" : roleColor}`,
                boxShadow: `0 0 0 4px ${isPending ? "#cbd5e122" : roleColor + "22"}, 0 4px 16px rgba(0,0,0,0.06)`,
              }}
            >
              <Avatar className="w-full h-full">
                <AvatarImage src={node.picture} alt={node.name} />
                <AvatarFallback
                  className="text-2xl font-bold"
                  style={{ backgroundColor: roleColor + "33", color: roleColor }}
                >
                  {node.name?.charAt(0) || "?"}
                </AvatarFallback>
              </Avatar>
            </div>
            <h2
              className="text-xl font-bold mt-3"
              style={{ fontFamily: "system-ui, sans-serif", color: "var(--color-text-primary)" }}
              data-testid="drawer-connection-name"
            >
              {node.name}
            </h2>
            {!editingRole ? (
              <button
                onClick={() => setEditingRole(true)}
                className="inline-flex items-center gap-1.5 mt-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium hover:ring-2 hover:ring-violet-300 transition"
                style={{ backgroundColor: roleColor + "33", color: roleColor, border: `1px solid ${roleColor}55` }}
                data-testid="drawer-role-badge"
              >
                {ROLE_LABELS[node.role] || "Other"}
                <Pencil className="w-3 h-3 opacity-60" />
              </button>
            ) : (
              <div className="mt-2 w-40">
                <Select value={node.role} onValueChange={handleRoleChange}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {isPending && (
              <span className="inline-flex items-center gap-1 mt-2 text-[11px] px-2 py-0.5 rounded-full" style={{ background: "rgba(251,191,36,0.14)", color: "#FCD34D", border: "1px solid rgba(251,191,36,0.30)" }}>
                <Clock className="w-3 h-3" />
                Invitation pending
              </span>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 py-5">
            <div className="rounded-xl p-3" style={{ background: "rgba(167,139,250,0.10)", border: "1px solid rgba(167,139,250,0.20)" }} data-testid="drawer-stat-insights">
              <div className="flex items-center gap-1.5 text-xs" style={{ color: "var(--color-text-secondary)" }}>
                <Sparkles className="w-3.5 h-3.5" style={{ color: "var(--color-brand-primary)" }} />
                Insights exchanged
              </div>
              <div className="text-2xl font-bold mt-1" style={{ color: "var(--color-text-primary)" }} data-testid="drawer-insight-count">
                {node.insight_count || 0}
              </div>
            </div>
            <div className="rounded-xl p-3" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid="drawer-stat-last">
              <div className="flex items-center gap-1.5 text-xs" style={{ color: "var(--color-text-secondary)" }}>
                <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "var(--color-text-placeholder)" }} />
                Last insight
              </div>
              <div className="text-sm font-semibold mt-2" style={{ color: "var(--color-text-primary)" }}>
                {lastInsight || <span style={{ color: "var(--color-text-placeholder)", fontWeight: 400 }}>None yet</span>}
              </div>
            </div>
          </div>

          {/* Vote summary with drilldown — only if they've voted */}
          {insightsData?.votes?.total > 0 && (
            <div className="rounded-xl border border-slate-100 p-3 mb-5" data-testid="drawer-vote-summary">
              <div className="text-xs text-slate-500 mb-2">
                Their take on your SWOT
              </div>
              <div className="space-y-1.5">
                {[
                  {
                    key: "agree", label: "Agree", Icon: ThumbsUp,
                    btnCls: "border-emerald-100 hover:bg-emerald-50 bg-white",
                    openCls: "bg-emerald-50",
                    iconCls: "text-emerald-600",
                    countCls: "text-emerald-700",
                    dotCls: "bg-emerald-400",
                  },
                  {
                    key: "disagree", label: "Disagree", Icon: ThumbsDown,
                    btnCls: "border-rose-100 hover:bg-rose-50 bg-white",
                    openCls: "bg-rose-50",
                    iconCls: "text-rose-600",
                    countCls: "text-rose-700",
                    dotCls: "bg-rose-400",
                  },
                  {
                    key: "unsure", label: "Unsure", Icon: HelpCircle,
                    btnCls: "border-slate-100 hover:bg-slate-50 bg-white",
                    openCls: "bg-slate-50",
                    iconCls: "text-slate-500",
                    countCls: "text-slate-700",
                    dotCls: "bg-slate-400",
                  },
                ].map(({ key, label, Icon, btnCls, openCls, iconCls, countCls, dotCls }) => {
                  const count = insightsData.votes.counts[key] || 0;
                  const items = insightsData.votes.grouped?.[key] || [];
                  const isOpen = openVoteGroup === key;
                  const disabled = count === 0;
                  return (
                    <div key={key}>
                      <button
                        type="button"
                        onClick={() => !disabled && setOpenVoteGroup(isOpen ? null : key)}
                        disabled={disabled}
                        className={`w-full flex items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 border transition-all ${
                          disabled ? "opacity-50 cursor-default border-slate-100 bg-white" : btnCls
                        } ${isOpen && !disabled ? openCls : ""}`}
                        data-testid={`drawer-vote-group-${key}`}
                      >
                        <span className="flex items-center gap-2">
                          <Icon className={`w-3.5 h-3.5 ${iconCls}`} />
                          <span className={`text-xs font-semibold ${countCls}`}>{count}</span>
                          <span className="text-xs text-slate-600">{label}</span>
                        </span>
                        {!disabled && (
                          <ChevronDown
                            className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                          />
                        )}
                      </button>
                      {isOpen && items.length > 0 && (
                        <ul className="mt-1.5 pl-2 space-y-1" data-testid={`drawer-vote-items-${key}`}>
                          {items.map((v) => (
                            <li
                              key={v.swot_item_id}
                              className="flex items-start gap-2 text-xs text-slate-700 leading-snug py-1"
                            >
                              <span className={`flex-shrink-0 mt-1 w-1.5 h-1.5 rounded-full ${dotCls}`} />
                              <div className="min-w-0 flex-1">
                                <p className="break-words">
                                  {v.item_text || <span className="italic text-slate-400">(item removed)</span>}
                                </p>
                                {v.category && (
                                  <span className="text-[10px] uppercase tracking-wide text-slate-400">
                                    {v.category}
                                  </span>
                                )}
                                {v.comment && (
                                  <p className="mt-1 text-xs text-slate-500 italic">
                                    &ldquo;{v.comment}&rdquo;
                                  </p>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Insights from them — text responses + structured SWOT suggestions */}
          {insightsLoading ? (
            <div className="text-xs text-slate-400 py-3" data-testid="drawer-insights-loading">
              Loading insights...
            </div>
          ) : insightsData?.responses?.length > 0 ? (
            <div className="mb-5" data-testid="drawer-insights-list">
              <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-2">
                <MessageSquare className="w-3.5 h-3.5 text-violet-500" />
                Insights from {node.name?.split(" ")[0]}
              </div>
              <div className="space-y-3">
                {insightsData.responses.map((r) => {
                  const swot = r.suggested_swot_items || {};
                  const hasSwotSugg =
                    (swot.strengths?.length || 0) + (swot.weaknesses?.length || 0) +
                    (swot.opportunities?.length || 0) + (swot.threats?.length || 0) +
                    (swot.unsure?.length || 0) > 0;
                  const hasValues = (r.suggested_values || []).length > 0;
                  const hasText = (r.response || "").length > 0;
                  const SWOT_CATS = [
                    { k: "strengths", label: "Strengths", cls: "text-emerald-700 bg-emerald-50 border-emerald-100" },
                    { k: "weaknesses", label: "Weaknesses", cls: "text-rose-700 bg-rose-50 border-rose-100" },
                    { k: "opportunities", label: "Opportunities", cls: "text-sky-700 bg-sky-50 border-sky-100" },
                    { k: "threats", label: "Threats", cls: "text-amber-700 bg-amber-50 border-amber-100" },
                    { k: "unsure", label: "Unsure about", cls: "text-slate-700 bg-slate-50 border-slate-200" },
                  ];
                  return (
                    <div
                      key={r.request_id}
                      className="rounded-lg p-3"
                      style={{
                        background: "rgba(18, 12, 36, 0.55)",
                        border: "1px solid rgba(167, 139, 250, 0.20)",
                        backdropFilter: "blur(8px)",
                      }}
                      data-testid={`drawer-insight-${r.request_id}`}
                    >
                      {hasText && (
                        <p className="text-sm whitespace-pre-wrap leading-relaxed mb-2" style={{ color: "var(--color-text-primary)" }}>
                          {r.response}
                        </p>
                      )}
                      {hasSwotSugg && (
                        <div className="space-y-2 mt-1">
                          {SWOT_CATS.map(({ k, label, cls }) => {
                            const items = swot[k] || [];
                            if (items.length === 0) return null;
                            return (
                              <div key={k} data-testid={`drawer-suggested-${k}`}>
                                <div className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${cls}`}>
                                  {label}
                                </div>
                                <ul className="mt-1 space-y-0.5 pl-3">
                                  {items.map((sug, idx) => (
                                    <li key={idx} className="text-xs leading-snug list-disc" style={{ color: "var(--color-text-primary)" }}>
                                      {sug}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {hasValues && (
                        <div className="mt-2" data-testid="drawer-suggested-values">
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-violet-700 bg-violet-50 border border-violet-100 rounded px-1.5 py-0.5 inline-block">
                            Values they see in you
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {r.suggested_values.map((v, idx) => (
                              <span
                                key={idx}
                                className="text-xs rounded-full px-2 py-0.5"
                                style={{
                                  color: "var(--color-text-primary)",
                                  background: "rgba(167,139,250,0.14)",
                                  border: "1px solid rgba(167,139,250,0.30)",
                                }}
                              >
                                {v}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {r.comment && (
                        <p className="mt-2 text-xs italic pl-2" style={{ color: "var(--color-text-secondary)", borderLeft: "2px solid rgba(167,139,250,0.30)" }}>
                          &ldquo;{r.comment}&rdquo;
                        </p>
                      )}
                      <div className="mt-2 text-[10px] uppercase tracking-wide" style={{ color: "var(--color-text-placeholder)" }}>
                        {r.tool_type} · {formatRelativeTime(r.created_at) || "recent"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Primary action */}
          <div className="pt-2">
            <Button
              onClick={() => {
                onOpenChange(false);
                onAskInsight?.(node);
              }}
              disabled={isPending}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-xl h-11 shadow-md shadow-violet-500/20"
              data-testid="drawer-ask-insight-btn"
            >
              <MessageCircleHeart className="w-4 h-4 mr-2" />
              Ask {node.name?.split(" ")[0] || "them"} for an insight
            </Button>

            {/* Minimalistic remove link */}
            <div className="flex justify-center mt-4 pb-2">
              <button
                onClick={() => setConfirmRemove(true)}
                className="text-xs text-slate-400 hover:text-red-500 transition-colors"
                data-testid="drawer-remove-btn"
              >
                Remove from circle
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Remove confirmation */}
      <AlertDialog open={confirmRemove} onOpenChange={setConfirmRemove}>
        <AlertDialogContent data-testid="drawer-remove-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {node.name} from your circle?</AlertDialogTitle>
            <AlertDialogDescription>
              You&apos;ll lose the ability to exchange insights. Any previous
              insights they&apos;ve shared will stay with you. This can&apos;t
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemove}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="drawer-remove-confirm-btn"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ConnectionDrawer;
